import { useTextDirection } from '@reactuses/core';
import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';
import { z } from 'zod';

import {
  type GetCurrentUser,
  updateMyIdentityDocumentSchema,
} from '@virtual-care/client';
import {
  Button,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  Star,
  TheButton,
  errorToFormIssue,
  useAction,
} from '@virtual-care/ui';

const documentTypeSchema = updateMyIdentityDocumentSchema.shape.documentType;
const documentTypes = documentTypeSchema.options;
type DocumentType = z.infer<typeof documentTypeSchema>;

const schema = updateMyIdentityDocumentSchema.extend({
  documentType: z.enum(documentTypes, {
    errorMap: () => ({ message: 'auth.insurance.documentTypeRequired' }),
  }),
  documentId: z
    .string()
    .trim()
    .min(1, { message: 'auth.insurance.documentIdRequired' }),
});

export default function Insurance() {
  const user = useLoaderData() as GetCurrentUser;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [textDirection] = useTextDirection();
  const updateDocument = useAction('PATCH /profile/document', {
    invalidate: ['GET /patients/me'],
  });

  const labels: Record<DocumentType, string> = {
    NATIONAL_ID: t('auth.insurance.documentTypes.national'),
    IQAMA: t('auth.insurance.documentTypes.iqama'),
    PASSPORT: t('auth.insurance.documentTypes.passport'),
  };

  const form = useForm({
    defaultValues: {
      documentType: user.b2cProfile?.documentType ?? undefined,
      documentId: user.b2cProfile?.documentId ?? '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({ action: 'save_identity_document' });
          await updateDocument.mutateAsync(schema.parse(value));
          await navigate('/', { replace: true });
          return void 0;
        } catch (error) {
          log.error({ action: 'save_identity_document', error });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      title={t('auth.insurance.title')}
      subtitle={t('auth.insurance.subtitle')}
      center
    >
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="documentType"
            children={(field) => (
              <div className="grid gap-2">
                <Label htmlFor="documentType" className="gap-1">
                  {t('auth.insurance.documentType')}
                  <Star />
                </Label>
                <Select
                  dir={textDirection === 'rtl' ? 'rtl' : 'ltr'}
                  value={field.state.value}
                  onValueChange={(value) =>
                    field.handleChange(documentTypeSchema.parse(value))
                  }
                >
                  <SelectTrigger
                    id="documentType"
                    className="w-full"
                    aria-required="true"
                  >
                    <SelectValue
                      placeholder={t('auth.insurance.selectDocumentType')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {labels[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="documentId"
            children={(field) => {
              const documentType = form.getFieldValue('documentType');

              return (
                <div className="grid gap-2">
                  <Label htmlFor="documentId" className="gap-1">
                    {documentType
                      ? labels[documentType]
                      : t('auth.insurance.documentId')}
                    <Star />
                  </Label>
                  <Input
                    id="documentId"
                    dir="ltr"
                    aria-required="true"
                    inputMode={documentType === 'PASSPORT' ? 'text' : 'numeric'}
                    autoComplete="off"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              );
            }}
          />

          <p className="text-muted-foreground text-sm">
            {t('auth.insurance.helper')}
          </p>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <div className="grid gap-2">
                <TheButton
                  type="submit"
                  loading={isSubmitting || updateDocument.isPending}
                  disabled={!canSubmit}
                >
                  {t('auth.insurance.save')}
                </TheButton>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/', { replace: true })}
                >
                  {t('auth.insurance.skip')}
                </Button>
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
